/**
 * Rate schedule API client functions
 *
 * Low-level API functions for rate schedule management.
 * These handle the HTTP requests and are consumed by React Query hooks.
 */

import { AxiosInstance } from "axios";

import {
  RateCategory,
  RateSchedule,
  CreateRateScheduleRequest,
  CopyRateSchedulesRequest,
  ListRateSchedulesResponse,
  UpdateRateScheduleRequest,
  ReorderRateSchedulesRequest,
} from "../types";

// Base URL for rate schedule APIs
const RATE_SCHEDULES_BASE_URL = "btx/v1/rate-schedules";

/**
 * Lists rate schedules for a benefit plan
 *
 * GET /api/btx/v1/rate-schedules?benefitPlanId={id}[&includeRenewal=true]
 *
 * By default only current-category schedules are returned. Pass includeRenewal to
 * also get the plan's renewal and revisedRenewal schedules (the PPX/MP opt-in read).
 *
 * @param benefitPlanId - Benefit plan ID
 * @param axiosInstance - Axios instance for API calls
 * @param includeRenewal - When true, include renewal/revisedRenewal schedules
 * @returns Promise resolving to list of rate schedules
 */
export const listRateSchedules = async (
  benefitPlanId: string,
  axiosInstance: AxiosInstance,
  includeRenewal?: boolean,
): Promise<ListRateSchedulesResponse> => {
  const query = includeRenewal
    ? `?benefitPlanId=${benefitPlanId}&includeRenewal=true`
    : `?benefitPlanId=${benefitPlanId}`;

  const response = await axiosInstance.get<
    { rateSchedules: ListRateSchedulesResponse } | ListRateSchedulesResponse
  >(`${RATE_SCHEDULES_BASE_URL}${query}`);

  // Handle both wrapped { rateSchedules: [...] } and bare array [...] responses
  const data = response.data;
  if (Array.isArray(data)) {
    return data;
  }
  return data.rateSchedules ?? [];
};

/**
 * Gets a single rate schedule by ID
 *
 * GET /api/btx/v1/rate-schedules/:id
 *
 * @param scheduleId - Rate schedule ID
 * @param axiosInstance - Axios instance for API calls
 * @returns Promise resolving to rate schedule
 */
export const getRateSchedule = async (
  scheduleId: string,
  axiosInstance: AxiosInstance,
): Promise<RateSchedule> => {
  const response = await axiosInstance.get<RateSchedule>(
    `${RATE_SCHEDULES_BASE_URL}/${scheduleId}`,
  );

  return response.data;
};

/**
 * Creates one or more rate schedules
 *
 * POST /api/btx/v1/rate-schedules
 *
 * @param request - Create rate schedule request with array of schedules
 * @param axiosInstance - Axios instance for API calls
 * @returns Promise resolving to array of created rate schedules
 */
export const createRateSchedule = async (
  request: CreateRateScheduleRequest,
  axiosInstance: AxiosInstance,
): Promise<RateSchedule[]> => {
  const response = await axiosInstance.post<{ rateSchedules: RateSchedule[] }>(
    RATE_SCHEDULES_BASE_URL,
    request,
  );

  return response.data.rateSchedules;
};

/**
 * Updates rate schedule metadata
 *
 * PATCH /api/btx/v1/rate-schedules/:id
 *
 * @param request - Update rate schedule request
 * @param axiosInstance - Axios instance for API calls
 * @returns Promise resolving to updated rate schedule
 */
export const updateRateSchedule = async (
  request: UpdateRateScheduleRequest,
  axiosInstance: AxiosInstance,
): Promise<RateSchedule> => {
  const { id, rateSchedule } = request;

  const response = await axiosInstance.patch<RateSchedule>(
    `${RATE_SCHEDULES_BASE_URL}/${id}`,
    rateSchedule,
  );

  return response.data;
};

/**
 * Deletes a rate schedule
 *
 * DELETE /api/btx/v1/rate-schedules/:id
 *
 * @param scheduleId - Rate schedule ID to delete
 * @param axiosInstance - Axios instance for API calls
 * @returns Promise resolving when deletion is complete
 */
export const deleteRateSchedule = async (
  scheduleId: string,
  axiosInstance: AxiosInstance,
): Promise<void> => {
  await axiosInstance.delete(`${RATE_SCHEDULES_BASE_URL}/${scheduleId}`);
};

/**
 * Deletes all rate schedules for a benefit plan
 *
 * DELETE /api/btx/v1/rate-schedules?benefitPlanId={id}
 *
 * @param benefitPlanId - Benefit plan ID
 * @param axiosInstance - Axios instance for API calls
 * @returns Promise resolving when deletion is complete
 */
export const deleteRateSchedulesByPlan = async (
  benefitPlanId: string,
  axiosInstance: AxiosInstance,
): Promise<void> => {
  await axiosInstance.delete(
    `${RATE_SCHEDULES_BASE_URL}?benefitPlanId=${benefitPlanId}`,
  );
};

/**
 * Clears a benefit plan's rate schedules within a single category (renewal or
 * revisedRenewal). Current rates cannot be cleared via this endpoint.
 *
 * DELETE /api/btx/v1/rate-schedules?benefitPlanId={id}&category={category}
 *
 * @param benefitPlanId - Benefit plan ID
 * @param category - Rate category to clear
 * @param axiosInstance - Axios instance for API calls
 * @returns Promise resolving when deletion is complete
 */
export const deleteRateSchedulesByCategory = async (
  benefitPlanId: string,
  category: RateCategory,
  axiosInstance: AxiosInstance,
): Promise<void> => {
  await axiosInstance.delete(
    `${RATE_SCHEDULES_BASE_URL}?benefitPlanId=${benefitPlanId}&category=${category}`,
  );
};

/**
 * Reorders rate schedules within a benefit plan
 *
 * PATCH /api/btx/v1/rate-schedules/reorder?benefitPlanId={id}
 *
 * @param request - Reorder request with benefit plan ID and ordered schedule IDs
 * @param axiosInstance - Axios instance for API calls
 * @returns Promise resolving to reordered schedules
 */
export const reorderRateSchedules = async (
  request: ReorderRateSchedulesRequest,
  axiosInstance: AxiosInstance,
): Promise<ListRateSchedulesResponse> => {
  const { benefitPlanId, rateSchedules } = request;

  const response = await axiosInstance.patch<ListRateSchedulesResponse>(
    `${RATE_SCHEDULES_BASE_URL}/reorder?benefitPlanId=${benefitPlanId}`,
    { rateSchedules },
  );

  return response.data;
};

/**
 * Copies all rate schedules from one benefit plan to another,
 * replacing any existing rate schedules on the destination plan.
 *
 * POST /api/btx/v1/rate-schedules/copy
 */
export const copyRateSchedules = async (
  request: CopyRateSchedulesRequest,
  axiosInstance: AxiosInstance,
): Promise<RateSchedule[]> => {
  const response = await axiosInstance.post<{ rateSchedules: RateSchedule[] }>(
    `${RATE_SCHEDULES_BASE_URL}/copy`,
    request,
  );

  return response.data.rateSchedules;
};
